import type {
  AccessRequestsListResponse,
  ApproveAccessRequestResponse,
  AccessRequestStatus,
  LlmConfigMetadata,
  LlmConfigUpsertRequest
} from './types';
import { getApiBaseUrl } from '../config';

const API_BASE = getApiBaseUrl(
  import.meta.env.VITE_ACCESS_ADMIN_API_URL ??
  import.meta.env.VITE_API_BASE_URL ??
  'http://localhost:4000'
);

export class AdminAuthError extends Error {
  constructor(message = 'Admin session expired or unauthorized') {
    super(message);
    this.name = 'AdminAuthError';
  }
}

function adminHeaders(token?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export interface FetchAccessRequestsParams {
  page?: number;
  pageSize?: number;
  status?: AccessRequestStatus | 'ALL';
}

export async function fetchAccessRequests(
  params: FetchAccessRequestsParams = {},
  token?: string
): Promise<AccessRequestsListResponse> {
  const search = new URLSearchParams();

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  search.set('page', String(page));
  search.set('pageSize', String(pageSize));

  if (params.status && params.status !== 'ALL') {
    search.set('status', params.status);
  }

  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/access-requests?${search.toString()}`;

  const res = await fetch(url, {
    headers: adminHeaders(token)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to fetch access requests: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function approveAccessRequest(
  id: string,
  token: string
): Promise<ApproveAccessRequestResponse> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/access-requests/${encodeURIComponent(
    id
  )}/approve`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({})
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to approve access request: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function renewAccessRequest(
  id: string,
  token: string
): Promise<ApproveAccessRequestResponse> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/access-requests/${encodeURIComponent(
    id
  )}/renew`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({})
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to renew access request token: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function rejectAccessRequest(id: string, token: string): Promise<void> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/access-requests/${encodeURIComponent(
    id
  )}/reject`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({})
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to reject access request: ${res.status} ${res.statusText} ${text}`
    );
  }
}

export async function login(
  username: string,
  password: string
): Promise<{ token: string; username: string }> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/login`;

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      text || `Login failed: ${res.status} ${res.statusText}`
    );
  }

  return res.json();
}

export interface UpdateTokenAuditRateLimitBody {
  maxRequests24h?: number | null;
  maxRequests7d?: number | null;
}

export async function updateTokenAuditRateLimit(
  tokenId: string,
  body: UpdateTokenAuditRateLimitBody,
  adminToken: string
): Promise<void> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/tokens/${encodeURIComponent(
    tokenId
  )}/audit-rate-limit`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(adminToken),
    body: JSON.stringify(body)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to update token audit rate limit: ${res.status} ${res.statusText} ${text}`
    );
  }
}

// --- BYOK (LLM config) admin helpers ---

export async function getTokenLlmConfig(
  adminToken: string,
  tokenId: string
): Promise<LlmConfigMetadata | null> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/tokens/${encodeURIComponent(
    tokenId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'GET',
    headers: adminHeaders(adminToken)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to fetch token LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function upsertTokenLlmConfig(
  adminToken: string,
  tokenId: string,
  body: LlmConfigUpsertRequest
): Promise<LlmConfigMetadata> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/tokens/${encodeURIComponent(
    tokenId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(adminToken),
    body: JSON.stringify(body)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to save token LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function deleteTokenLlmConfig(
  adminToken: string,
  tokenId: string
): Promise<void> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/tokens/${encodeURIComponent(
    tokenId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'DELETE',
    headers: adminHeaders(adminToken)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok && res.status !== 404) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to delete token LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }
}

export async function getOrgLlmConfig(
  adminToken: string,
  orgId: string
): Promise<LlmConfigMetadata | null> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/orgs/${encodeURIComponent(
    orgId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'GET',
    headers: adminHeaders(adminToken)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to fetch org LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function upsertOrgLlmConfig(
  adminToken: string,
  orgId: string,
  body: LlmConfigUpsertRequest
): Promise<LlmConfigMetadata> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/orgs/${encodeURIComponent(
    orgId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(adminToken),
    body: JSON.stringify(body)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to save org LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }

  return res.json();
}

export async function deleteOrgLlmConfig(
  adminToken: string,
  orgId: string
): Promise<void> {
  const url = `${API_BASE.replace(/\/$/, '')}/api/admin/orgs/${encodeURIComponent(
    orgId
  )}/llm-config`;

  const res = await fetch(url, {
    method: 'DELETE',
    headers: adminHeaders(adminToken)
  });

  if (res.status === 401) {
    const text = await res.text().catch(() => '');
    throw new AdminAuthError(text || 'Admin session expired or unauthorized');
  }

  if (!res.ok && res.status !== 404) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Failed to delete org LLM config: ${res.status} ${res.statusText} ${text}`
    );
  }
}
